import { ButtonHTMLAttributes, ReactNode } from 'react';
import { ArrowIconLight, Css, Responsive, StyledButton } from '@/ui';
import styled from '@emotion/styled';
import { Color } from '../../../styles/Color';
import { theme } from '../../../styles/theme';

export interface IButtonBaseProps {
	buttonType?: 'primary' | 'secondary';
	icon?: ReactNode;
	iconPosition?: 'left' | 'right';
}

interface IButtonProps
	extends Omit<
			ButtonHTMLAttributes<HTMLButtonElement>,
			'dangerouslySetInnerHTML'
		>,
		IButtonBaseProps {
	label?: string | ReactNode;
	ariaLabel?: string;
}

export default function Button({
	buttonType = 'primary',
	label,
	icon,
	iconPosition = 'right',
	disabled,
	ariaLabel,
	...props
}: IButtonProps) {
	return (
		<ButtonStyled
			secondary={buttonType === 'secondary'}
			disabled={disabled}
			aria-label={typeof label === 'string' ? label : ariaLabel}
			tabIndex={disabled ? -1 : 0}
			iconPosition={iconPosition}
			{...props}
		>
			{label && <ButtonLabel>{label}</ButtonLabel>}
			{icon ?? <ArrowIconLight />}
		</ButtonStyled>
	);
}

const ButtonStyled = styled(StyledButton)<IButtonProps>`
	display: flex;
	flex-direction: ${({ iconPosition }) =>
		iconPosition === 'left' ? 'row-reverse' : 'row'};
	align-items: center;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		// Need to override base button padding for mobile
		padding: ${theme.button?.padding} !important;
	}

	svg {
		path {
			fill: currentColor;
		}
	}

	&:disabled {
		svg {
			path {
				fill: ${Color.Neutral30};
			}
		}
	}
`;

export const ButtonLabel = styled.div`
	font-size: ${Css.pxToRemFromTheme(14)};
	letter-spacing: 0.06em;
	line-height: normal;

	display: inline-block;
	max-width: ${Css.pxToRemFromTheme(295)};
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
`;
